import { useState, useEffect, useRef } from "react";
import { FlaskConical, Loader2, CheckCircle2, XCircle, AlertTriangle, ChevronDown, ChevronRight, Edit2, Blocks, Code2, Mail, Send, CalendarClock, Wand2 } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Input } from "../ui/input";
import { Switch } from "../ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { toast } from "sonner";
import { isBlockMode, modeLabel } from "../../lib/modes";
import { SysFixPanel } from "./SysFixPanel";

const API = "/api";

const GROUPS = [
  { key: "failing", label: "Sample solution fails the tests", tone: "red", Icon: XCircle },
  { key: "blocks_failing", label: "Block solution fails the tests", tone: "red", Icon: Blocks },
  { key: "no_solution", label: "No sample solution", tone: "amber", Icon: AlertTriangle },
  { key: "no_tests", label: "No test data (can't be graded)", tone: "amber", Icon: AlertTriangle },
];

function FailureList({ failures }) {
  if (!failures?.length) return null;
  return (
    <ul className="mt-2 space-y-1.5">
      {failures.map((f) => (
        <li key={f.test} className="text-xs font-mono bg-muted/50 rounded px-2 py-1.5 grid gap-0.5">
          <span className="text-muted-foreground">Test {f.test} · input: <span className="text-foreground">{f.input || "(empty)"}</span></span>
          <span>expected: <span className="text-emerald-600 dark:text-emerald-400">{f.expected}</span></span>
          {f.error ? <span>error: <span className="text-red-600 dark:text-red-400">{f.error}</span></span> : <span>got: <span className="text-red-600 dark:text-red-400">{f.got === "" ? "(no output)" : f.got}</span></span>}
        </li>
      ))}
    </ul>
  );
}

function ResultItem({ item, group, onEdit, onFixSys, fixingSys }) {
  const [open, setOpen] = useState(false);
  const failures = group === "blocks_failing" ? item.blocks_failures : item.failures;
  const expandable = failures?.length > 0;
  const sysBlocked = group === "failing" && failures?.some((f) => /blocked operation: sys|import not allowed: sys/i.test(f.error || ""));
  return (
    <li className="bg-card rounded-lg border border-border/60 px-3 py-2" data-testid={`solution-check-item-${group}-${item.question_id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <button type="button" onClick={() => expandable && setOpen(!open)} className={`flex items-center gap-1.5 text-sm font-medium text-left flex-1 min-w-0 ${expandable ? "hover:underline" : "cursor-default"}`} data-testid={`solution-check-toggle-${item.question_id}`}>
          {expandable ? (open ? <ChevronDown className="w-3.5 h-3.5 shrink-0" /> : <ChevronRight className="w-3.5 h-3.5 shrink-0" />) : <span className="w-3.5" />}
          <span className="truncate">{item.title}</span>
        </button>
        <Badge variant="outline" className="text-[10px] gap-1">{isBlockMode(item.mode) ? <Blocks className="w-3 h-3" /> : <Code2 className="w-3 h-3" />}{modeLabel(item.mode)}</Badge>
        <Badge variant="outline" className="text-[10px]">{item.pool === "daily" ? "Warm Up" : "Challenge"}</Badge>
        {item.total > 0 && group !== "no_tests" && (
          <span className="text-xs text-muted-foreground">{item.passed}/{item.total} tests</span>
        )}
        {sysBlocked && onFixSys && (
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-violet-600" disabled={fixingSys === item.question_id} onClick={() => onFixSys(item.question_id)} data-testid={`solution-check-fix-sys-${item.question_id}`}>
            {fixingSys === item.question_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Wand2 className="w-3.5 h-3.5" />} Strip sys
          </Button>
        )}
        {onEdit && (
          <Button variant="ghost" size="sm" className="h-7 gap-1 px-2" onClick={() => onEdit(item.question_id)} data-testid={`solution-check-edit-${item.question_id}`}>
            <Edit2 className="w-3.5 h-3.5" /> Edit
          </Button>
        )}
      </div>
      {open && <FailureList failures={failures} />}
    </li>
  );
}

const fmtWhen = (iso) => (iso ? new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }) : null);

function WeeklyEmailPanel({ running, onSendNow, lastEmail }) {
  const [settings, setSettings] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await fetch(`${API}/admin/health-email/settings`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setSettings(data);
        setRecipientsText((data.recipients || []).join(", "));
      }
    } catch { /* leave panel in loading state */ }
  };
  useEffect(() => { load(); }, []);
  // refresh "last sent" after a send-now completes
  useEffect(() => { if (lastEmail?.sent_to) load(); }, [lastEmail]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const recipients = recipientsText.split(/[,;\s]+/).map((r) => r.trim()).filter(Boolean);
      const res = await fetch(`${API}/admin/health-email/settings`, {
        method: "PUT", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ enabled: settings.enabled, recipients, ...patch }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Could not save"); return; }
      setSettings(data);
      setRecipientsText((data.recipients || []).join(", "));
      toast.success(patch.enabled === undefined ? "Recipients saved" : data.enabled ? "Weekly health email turned on" : "Weekly health email turned off");
    } catch {
      toast.error("Could not save");
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return null;
  const effective = settings.recipients?.length ? settings.recipients : settings.default_recipients || [];
  const dirty = recipientsText.trim() !== (settings.recipients || []).join(", ");

  return (
    <div className="mt-5 rounded-lg border border-border/60 bg-muted/20 p-4 space-y-3" data-testid="health-email-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0">
          <p className="font-medium flex items-center gap-2"><Mail className="w-4 h-4 text-violet-500" /> Weekly health email</p>
          <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> {settings.schedule} — runs the full check, then emails the summary (new vs fixed since the last report).</p>
        </div>
        <label className="flex items-center gap-2 text-sm">
          <span className={settings.enabled ? "text-emerald-600" : "text-muted-foreground"} data-testid="health-email-status">{settings.enabled ? "On" : "Off"}</span>
          <Switch checked={settings.enabled} disabled={saving} onCheckedChange={(v) => save({ enabled: v })} data-testid="health-email-toggle" />
        </label>
      </div>
      <div className="grid gap-2 sm:grid-cols-[1fr_auto] sm:items-end">
        <div>
          <label className="text-xs font-medium text-muted-foreground block mb-1">Send to <span className="font-normal">(comma-separated; leave empty for every master admin)</span></label>
          <Input value={recipientsText} onChange={(e) => setRecipientsText(e.target.value)} placeholder={(settings.default_recipients || []).join(", ")} className="h-9 text-sm" data-testid="health-email-recipients" />
        </div>
        <Button variant="outline" size="sm" disabled={!dirty || saving} onClick={() => save({})} data-testid="health-email-save-recipients">Save recipients</Button>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap text-xs text-muted-foreground">
        <span data-testid="health-email-last-sent">
          {settings.last_sent_at
            ? <>Last sent {fmtWhen(settings.last_sent_at)} to {(settings.last_sent_to || []).join(", ")} · {settings.last_flagged} flagged</>
            : <>Not sent yet · will go to {effective.join(", ") || "—"}</>}
        </span>
        <Button size="sm" variant="outline" disabled={running} onClick={onSendNow} className="gap-1.5 border-violet-400 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/40" data-testid="health-email-send-now">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} Run check &amp; email now
        </Button>
      </div>
      {lastEmail?.error && <p className="text-xs text-red-600" data-testid="health-email-error">{lastEmail.error}</p>}
    </div>
  );
}

export function SolutionCheckTool({ onEditQuestion, onFinished }) {
  const [scope, setScope] = useState("all");
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [fixingSys, setFixingSys] = useState(null);
  const [sysRefreshKey, setSysRefreshKey] = useState(0);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    const res = await fetch(`${API}/admin/solution-check`, { credentials: "include" });
    if (!res.ok) throw new Error("status");
    return res.json();
  };

  const stopPolling = () => { if (pollRef.current) { clearTimeout(pollRef.current); pollRef.current = null; } };

  const poll = async () => {
    try {
      const data = await fetchStatus();
      setJob(data);
      if (data.status === "running" || (data.status === "done" && data.email_after && !data.email)) {
        pollRef.current = setTimeout(poll, 2000);
        return;
      }
      setRunning(false);
      if (data.status === "done") {
        onFinished?.();
        if ((data.total_flagged || 0) === 0) toast.success(`All ${data.total} sample solutions pass their tests!`);
        else toast.warning(`Solution check complete — ${data.total_flagged} question(s) flagged`);
        if (data.email?.sent_to) toast.success(`Health email sent to ${data.email.sent_to.join(", ")}`);
        else if (data.email?.error) toast.error(data.email.error);
      } else if (data.status === "failed") {
        toast.error(data.error || "Solution check failed");
      }
    } catch {
      setRunning(false);
      toast.error("Could not read solution check status");
    }
  };

  useEffect(() => {
    fetchStatus().then((data) => {
      setJob(data);
      if (data.status === "running") { setRunning(true); pollRef.current = setTimeout(poll, 1500); }
    }).catch(() => {});
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async ({ email = false } = {}) => {
    setRunning(true);
    try {
      const url = email ? `${API}/admin/health-email/send` : `${API}/admin/solution-check?scope=${scope}`;
      const res = await fetch(url, { method: "POST", credentials: "include" });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Could not start the check");
        setRunning(false);
        return;
      }
      setJob(await res.json());
      pollRef.current = setTimeout(poll, 1500);
    } catch {
      toast.error("Could not start the check");
      setRunning(false);
    }
  };

  const done = job?.status === "done";
  const progressPct = job?.total ? Math.round(((job.checked || 0) / job.total) * 100) : 0;

  const refreshJob = () => { fetchStatus().then(setJob).catch(() => {}); onFinished?.(); };

  const fixSys = async (questionId) => {
    setFixingSys(questionId);
    try {
      const res = await fetch(`${API}/admin/questions/${questionId}/sys-fix`, { method: "POST", credentials: "include" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(data.detail || "Could not fix this solution"); return; }
      if (data.status === "fixed") { toast.success(`${data.title} - sample solution fixed and passes its tests`); refreshJob(); setSysRefreshKey((k) => k + 1); }
      else if (data.status === "not_needed") toast.info(`${data.title} doesn't use sys`);
      else toast.error(`${data.title}: ${data.reason}`);
    } catch {
      toast.error("Could not fix this solution");
    } finally {
      setFixingSys(null);
    }
  };

  return (
    <div className="border border-border rounded-xl p-4" data-testid="solution-check-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[240px]">
          <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-violet-500" />
            Check Sample Solutions
          </h3>
          <p className="text-sm text-muted-foreground mb-3">
            Runs every question's sample solution through the same sandbox and test cases students are graded with, and flags any that don't pass, have no solution, or have no test data. Results are saved on each question and shown in the Questions tab.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={scope} onValueChange={setScope} disabled={running}>
            <SelectTrigger className="w-[150px]" data-testid="solution-check-scope">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All questions</SelectItem>
              <SelectItem value="python">Python only</SelectItem>
              <SelectItem value="blocks">Blocks only</SelectItem>
              <SelectItem value="junior">Junior Blocks only</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={() => start()} disabled={running} variant="outline" className="gap-2 border-violet-400 text-violet-600 hover:bg-violet-50 dark:hover:bg-violet-950/40" data-testid="run-solution-check-btn">
            {running ? <><Loader2 className="w-4 h-4 animate-spin" />Checking…</> : <><FlaskConical className="w-4 h-4" />Run Check</>}
          </Button>
        </div>
      </div>

      {running && (
        <div className="mt-3" data-testid="solution-check-progress">
          <div className="flex items-center justify-between text-sm text-muted-foreground mb-1">
            <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> {job?.status === "done" && job?.email_after ? "Sending the health email…" : `Checking ${job?.scope && job.scope !== "all" ? `${job.scope} ` : ""}questions…`}</span>
            <span>{job?.checked || 0} / {job?.total || "…"}</span>
          </div>
          <div className="h-2 rounded-full bg-muted overflow-hidden">
            <div className="h-full bg-violet-500 transition-all duration-500" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      <WeeklyEmailPanel running={running} onSendNow={() => start({ email: true })} lastEmail={job?.email || null} />

      <SysFixPanel disabled={running} onFixed={refreshJob} onEditQuestion={onEditQuestion} refreshKey={sysRefreshKey} />

      {done && !running && (
        <div className="mt-4 space-y-3" data-testid="solution-check-results">
          <div className={`border rounded-lg p-3 text-sm flex items-center gap-2 ${job.total_flagged === 0 ? "bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800 text-green-700 dark:text-green-400" : "bg-muted/40 border-border"}`}>
            <CheckCircle2 className={`w-4 h-4 ${job.total_flagged === 0 ? "" : "text-emerald-500"}`} />
            <span>
              <strong>{job.passing ?? 0}</strong> of <strong>{job.total}</strong> {job.scope && job.scope !== "all" ? `${job.scope} ` : ""}questions pass all their tests
              {job.total_flagged > 0 && <> · <strong className="text-red-600 dark:text-red-400">{job.total_flagged}</strong> flagged</>}
              {job.finished_at && <span className="text-muted-foreground"> · checked {new Date(job.finished_at).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" })}</span>}
            </span>
          </div>
          {GROUPS.map(({ key, label, tone, Icon }) => {
            const items = job.results?.[key] || [];
            if (!items.length) return null;
            return (
              <div key={key} className={`border rounded-lg p-3 ${tone === "red" ? "bg-red-50/60 dark:bg-red-900/10 border-red-200 dark:border-red-800" : "bg-amber-50/60 dark:bg-amber-900/10 border-amber-200 dark:border-amber-800"}`} data-testid={`solution-check-group-${key}`}>
                <p className={`text-xs font-semibold uppercase tracking-wider mb-2 flex items-center gap-1.5 ${tone === "red" ? "text-red-700 dark:text-red-400" : "text-amber-700 dark:text-amber-400"}`}>
                  <Icon className="w-3.5 h-3.5" /> {label} ({items.length})
                </p>
                <ul className="space-y-1.5">
                  {items.map((item) => <ResultItem key={`${key}-${item.question_id}`} item={item} group={key} onEdit={onEditQuestion} onFixSys={fixSys} fixingSys={fixingSys} />)}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
